import {
  ChangeDetectorRef,
  Component,
  ViewChild,
  ViewEncapsulation,
  ElementRef,
  DestroyRef,
  inject,
} from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDrawer } from '@angular/material/sidenav';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { debounceTime, filter, map, switchMap, noop, from, mergeMap, finalize } from 'rxjs';
import { FileSystemFileEntry, NgxFileDropEntry } from 'ngx-file-drop';
import { AccountFoldersService } from 'app/modules/folders/folders.service';
import { ViewFileComponent } from 'app/modules/folders/view-file/view-file.component';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { CreateFolderComponent } from 'app/modules/folders/create-folder/create-folder.component';
import { FileResource, FolderResource, FoldersSearchResult } from 'app/shared/modules/folders/folders.types';
import { FileUploadEvent } from 'app/modules/projects/details/offers/offers.types';

@Component({
  selector: 'folders-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FoldersListComponent {
  folder: FolderResource;
  childFolders: FolderResource[];
  files: FileResource[];
  filesCount: any;
  selectedFile: FileResource;
  loading: boolean = false;
  searchInputControl: UntypedFormControl;
  results: FoldersSearchResult = { file_resources: [], folder_resources: [] };
  dragNode: any;
  dragNodeExpandOverWaitTimeMs = 300;
  dragNodeExpandOverNode: any;
  dragNodeExpandOverTime: number;
  dragNodeExpandOverArea: string;
  drawerMode: 'over' | 'side';
  drawerOpened: boolean;

  @ViewChild('emptyItem') emptyItem: ElementRef;
  @ViewChild('matDrawer', { static: true }) matDrawer: MatDrawer;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private accountFoldersService: AccountFoldersService,
    private _matDialog: MatDialog,
    private _changeDetectorRef: ChangeDetectorRef,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private confirmService: ConfirmService,
  ) {
    this.filesCount = {
      completed: 0,
      incomplete: 0,
      total: 0,
    };

    this.drawerMode = 'side';
    this.drawerOpened = true;

    this.searchInputControl = new UntypedFormControl();
  }

  ngOnInit(): void {
    this._activatedRoute.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: Data) => {
      if (this._activatedRoute.snapshot['_routerState'].url === '/folders') {
        this._router.navigate(['./', data.folder.id], { relativeTo: this._activatedRoute });
      }
    });

    this.accountFoldersService.folder$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((folder: FolderResource) => {
      this.folder = folder;
      this.childFolders = folder.children;
      this.files = folder.file_resources;
      this._changeDetectorRef.markForCheck();
    });

    this.searchInputControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(500),
        switchMap((query) => {
          return this.accountFoldersService.searchFolders(query);
        }),
        map((response) => {
          this.results = response;
          this._changeDetectorRef.markForCheck();
        }),
      )
      .subscribe(noop);
  }

  handleDragStart(event, resource): void {
    // Required by Firefox (https://stackoverflow.com/questions/19055264/why-doesnt-html5-drag-and-drop-work-in-firefox)
    event.dataTransfer.setData('foo', 'bar');
    event.target.style.opacity = '0.4';
    event.dataTransfer.setDragImage(this.emptyItem.nativeElement, 0, 0);
    this.dragNode = resource;
  }

  handleDragOver(event, resource): void {
    event.target.style.opacity = '0.5';
    event.preventDefault();
  }

  handleDragLeave(event) {
    event.target.style.opacity = '1';
    event.preventDefault();
  }

  handleDrop(event, resource): void {
    event.preventDefault();
    event.target.style.opacity = '1';
    if (!this.dragNode) {
      const itemsKeys = Object.keys(event.dataTransfer.items);
      const files = [];
      itemsKeys.forEach((key) => {
        const entryFile = event.dataTransfer.items[key].webkitGetAsEntry();
        files.push(new NgxFileDropEntry(entryFile.name, entryFile));
      });

      this.onFileDropped(files, resource.folder_resource_id || resource.id);
    } else if (this.dragNode !== resource) {
      //moving a folder within another folder
      if (!this.dragNode.folder_resource_id && !resource.folder_resource_id) {
        let folder = this.dragNode;
        folder.parent_id = resource.id;
        this.accountFoldersService
          .updateFolder(folder)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.accountFoldersService
              .getFolder(this.folder.id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(() => {
                this.loading = false;
              });
          });
      } else if (this.dragNode.folder_resource_id && !resource.folder_resource_id) {
        let file = this.dragNode;
        let oldFolderId = file.folder_resource_id;
        file.folder_resource_id = resource.id;

        this.accountFoldersService
          .updateFile(oldFolderId, file)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.accountFoldersService
              .getFolder(this.folder.id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(() => {
                this.loading = false;
              });
          });
      }
    }

    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  handleDragEnd(event): void {
    event.target.style.opacity = '1';
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
  }

  deleteFile(fileId: number): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        filter((res: boolean) => res),
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.accountFoldersService.deleteFile(this.folder.id, fileId)),
      )
      .subscribe(noop);
  }

  onFileChanged(event: Event & FileUploadEvent, folderId?: number): void {
    this.loading = true;
    let file = event.target.files[0];
    let fileResource = {
      folder_resource_id: folderId,
      name: file.name,
    };

    this.accountFoldersService
      .createFile(folderId, fileResource)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((fileResource) => this.accountFoldersService.uploadDocument(folderId, fileResource.id, file)),
        map(() => {
          this.loading = false;
          event.target.value = '';
        }),
      )
      .subscribe(noop);
  }

  onFileDropped(files: NgxFileDropEntry[], folderId?: number): void {
    this.loading = true;

    // Process each dropped file
    from(files)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((droppedFile) => (droppedFile.fileEntry as FileSystemFileEntry).isFile), // Only process files, skip directories
        mergeMap((droppedFile) => {
          const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
          // Convert the fileEntry.file() callback to an Observable
          return from(new Promise<File>((resolve) => fileEntry.file(resolve))).pipe(
            map((file) => ({
              file,
              fileResource: {
                folder_resource_id: folderId,
                name: fileEntry.name,
              },
            })),
          );
        }),
        mergeMap(({ file, fileResource }) =>
          this.accountFoldersService
            .createFile(folderId, fileResource)
            .pipe(
              switchMap((createdResource) =>
                this.accountFoldersService.uploadDocument(folderId, createdResource.id, file),
              ),
            ),
        ),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe(noop);
  }

  editFolderResource(folderResource: FolderResource): void {
    this._matDialog
      .open(CreateFolderComponent, {
        data: {
          folderResource: folderResource,
        },
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
        switchMap(() => this.accountFoldersService.getFolder(this.folder.id)),
      )
      .subscribe(noop);
  }

  createFolder(folderId: number): void {
    this._matDialog
      .open(CreateFolderComponent, {
        data: {
          folderId: folderId,
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.folder) {
          this.navigateToFolder(result.folder);
        }
      });
  }

  private navigateToFolder(childFolder: FolderResource): void {
    if (this._activatedRoute.snapshot.params?.folderId) {
      this._router.navigate(['../', childFolder.id], { relativeTo: this._activatedRoute });
    } else {
      this._router.navigate([childFolder.id], { relativeTo: this._activatedRoute });
    }
  }

  viewFile(fileResource: FileResource): void {
    if (fileResource.documents[0].attachment_content_type.includes('csv')) {
      this.downloadFile(fileResource);
    } else {
      this._matDialog.open(ViewFileComponent, {
        minWidth: '100vw',
        height: '100vh',
        data: {
          fileResource: fileResource,
        },
      });
    }
  }

  private downloadFile(file: FileResource): void {
    window.open(file.document_url, '_blank');
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
